import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { track } from "@/lib/analytics";

// Intelligent, context-aware end-of-demo prompt. Shows ONLY after meaningful engagement
// (>=3 module visits or >=60s exploring), once per session, never blocking product UI.
// Demo-only. Objective: convert a genuinely-interested visitor directly to SUBSCRIBE.
export function DemoConversionPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const [visits, setVisits] = useState(0);
  const [show, setShow] = useState(false);

  useEffect(() => { setVisits((v) => v + 1); }, [location.pathname]);

  // Modal guard: never let the conversion prompt overlap the reservation setup / connection flow.
  const onSetup = location.pathname.startsWith("/reservations");
  useEffect(() => { if (onSetup) setShow(false); }, [onSetup]);

  useEffect(() => {
    try { if (sessionStorage.getItem("ari_demo_prompt_seen")) return; } catch (_) {}
    const timer = setTimeout(() => trigger(), 60000);
    if (visits >= 3) trigger();
    function trigger() {
      if (location.pathname.startsWith("/reservations")) return; // don't block setup
      try { if (sessionStorage.getItem("ari_demo_prompt_seen")) return; sessionStorage.setItem("ari_demo_prompt_seen", "1"); } catch (_) {}
      setShow(true);
      track("demo_prompt_shown");
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visits]);

  const dismiss = () => { track("demo_prompt_dismissed"); setShow(false); };
  const subscribe = () => { track("subscribe_click", { source: "demo_prompt" }); navigate("/billing"); };

  return (
    <div className={`fixed z-[85] bottom-5 right-5 max-w-sm transition-all duration-500 ${show ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-8 pointer-events-none"}`}
      data-testid="demo-conversion-prompt">
      <div className="relative rounded-2xl border border-[#D4AF37]/30 bg-[#141416]/95 backdrop-blur-xl p-5 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
        <button onClick={dismiss} data-testid="demo-prompt-dismiss" className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 text-[#D4AF37] mb-2"><Sparkles className="w-4 h-4" /><span className="font-mono-lux uppercase tracking-[0.2em] text-[10px]">You've seen the platform</span></div>
        <p className="font-serif-lux text-2xl leading-tight text-white mb-1.5">Ready to get this for your restaurant?</p>
        <p className="text-white/50 text-sm mb-4">Everything you just explored is available on one all-inclusive plan.</p>
        <div className="flex flex-col gap-2">
          <button onClick={subscribe} data-testid="demo-prompt-subscribe" className="gold-btn get-ari-shine rounded-lg py-2.5 text-sm font-semibold">Get ARI</button>
          <button onClick={dismiss} data-testid="demo-prompt-continue" className="text-white/40 hover:text-white/70 text-xs py-1.5 transition-colors">Not now — continue exploring</button>
        </div>
      </div>
    </div>
  );
}
